/**
 * useRecording — Hook for interactive recording in Watch Mode.
 *
 * When recording is active:
 * - Click in CDP view -> capture selector -> add Click step
 * - Type text -> capture as Type step
 * - Navigate -> capture as Navigate step
 * - Element picked -> can optionally add as an assertion step
 *
 * Collected steps can be appended to the step editor.
 */

import { useState, useCallback, useRef } from 'react';

/**
 * Rank multiple candidate selectors by reliability.
 * Prefers: id > data-test > aria-label > unique class > nth-child
 */
const rankSelectors = (elementInfo) => {
  if (!elementInfo) return [];

  const candidates = [];
  const { tagName, id, className, attributes, selector } = elementInfo;
  const tag = tagName || 'div';

  // 1. ID selector (most reliable)
  if (id) {
    candidates.push({ selector: `#${id}`, confidence: 0.95, source: 'id' });
  }

  // 2. data-test-id / data-testid attributes
  if (attributes) {
    for (const attr of ['data-testid', 'data-test-id', 'data-cy']) {
      if (attributes[attr]) {
        candidates.push({
          selector: `${tag}[${attr}="${attributes[attr]}"]`,
          confidence: 0.9,
          source: attr,
        });
      }
    }

    // 3. aria-label
    if (attributes['aria-label']) {
      candidates.push({
        selector: `${tag}[aria-label="${attributes['aria-label']}"]`,
        confidence: 0.85,
        source: 'aria-label',
      });
    }

    // 4. name attribute
    if (attributes['name']) {
      candidates.push({
        selector: `${tag}[name="${attributes['name']}"]`,
        confidence: 0.8,
        source: 'name',
      });
    }

    // 5. type + role combination
    if (attributes['type'] && attributes['role']) {
      candidates.push({
        selector: `${tag}[type="${attributes['type']}"][role="${attributes['role']}"]`,
        confidence: 0.7,
        source: 'type+role',
      });
    }
  }

  // 6. Class-based selector (if unique-looking classes)
  if (className && typeof className === 'string') {
    const classes = className.split(/\s+/).filter((c) => c.length > 2 && c.length < 30);
    if (classes.length > 0) {
      candidates.push({
        selector: `${tag}.${classes.join('.')}`,
        confidence: 0.6,
        source: 'class',
      });
    }
  }

  // 7. Fall back to the server-generated selector
  if (selector && !candidates.some((c) => c.selector === selector)) {
    candidates.push({
      selector,
      confidence: 0.5,
      source: 'auto',
    });
  }

  // Sort by confidence descending
  candidates.sort((a, b) => b.confidence - a.confidence);
  return candidates;
};

export default function useRecording() {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedSteps, setRecordedSteps] = useState([]);
  const stepCountRef = useRef(0);
  const lastUrlRef = useRef('');

  const startRecording = useCallback(() => {
    setIsRecording(true);
    setRecordedSteps([]);
    stepCountRef.current = 0;
  }, []);

  const stopRecording = useCallback(() => {
    setIsRecording(false);
  }, []);

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  // Record a click action from picked element
  const recordClick = useCallback(
    (elementInfo) => {
      if (!isRecording || !elementInfo) return null;

      const candidates = rankSelectors(elementInfo);
      const bestSelector = candidates.length > 0 ? candidates[0].selector : elementInfo.selector || '';

      // Determine if this is an input/textarea (might be better as a future Type step target)
      const isInput = ['input', 'textarea', 'select'].includes(elementInfo.tagName);
      const stepName = isInput
        ? `Click on ${elementInfo.tagName} field`
        : `Click ${elementInfo.textContent ? `"${elementInfo.textContent.substring(0, 40)}"` : bestSelector}`;

      stepCountRef.current += 1;
      const step = {
        stepNumber: stepCountRef.current,
        type: 'action',
        name: stepName,
        inputData: {
          action: 'click',
          selector: bestSelector,
          timeout: 3000,
        },
        _recording: {
          candidates,
          elementInfo,
          timestamp: Date.now(),
        },
      };

      setRecordedSteps((prev) => [...prev, step]);
      return step;
    },
    [isRecording]
  );

  // Record a type action
  const recordType = useCallback(
    (text, elementInfo) => {
      if (!isRecording) return null;

      const candidates = elementInfo ? rankSelectors(elementInfo) : [];
      const bestSelector = candidates.length > 0 ? candidates[0].selector : '';

      stepCountRef.current += 1;
      const step = {
        stepNumber: stepCountRef.current,
        type: 'action',
        name: `Type "${text.substring(0, 30)}${text.length > 30 ? '...' : ''}"`,
        inputData: {
          action: 'type',
          selector: bestSelector,
          text,
          clearFirst: true,
          timeout: 3000,
        },
        _recording: {
          candidates,
          elementInfo,
          timestamp: Date.now(),
        },
      };

      setRecordedSteps((prev) => [...prev, step]);
      return step;
    },
    [isRecording]
  );

  // Record a navigation action
  const recordNavigate = useCallback(
    (url) => {
      if (!isRecording) return null;
      if (url === lastUrlRef.current) return null; // Skip duplicate

      lastUrlRef.current = url;
      stepCountRef.current += 1;
      const step = {
        stepNumber: stepCountRef.current,
        type: 'action',
        name: `Navigate to ${new URL(url).hostname}`,
        inputData: {
          action: 'navigate',
          url,
          timeout: 10000,
        },
        _recording: {
          timestamp: Date.now(),
        },
      };

      setRecordedSteps((prev) => [...prev, step]);
      return step;
    },
    [isRecording]
  );

  // Record a wait step (manually triggered)
  const recordWait = useCallback(
    (selector, timeout = 5000) => {
      if (!isRecording) return null;

      stepCountRef.current += 1;
      const step = {
        stepNumber: stepCountRef.current,
        type: 'wait',
        name: `Wait for ${selector}`,
        inputData: {
          waitType: 'selector',
          selector,
          timeout,
        },
        _recording: {
          timestamp: Date.now(),
        },
      };

      setRecordedSteps((prev) => [...prev, step]);
      return step;
    },
    [isRecording]
  );

  // Clear a specific recorded step
  const removeRecordedStep = useCallback((index) => {
    setRecordedSteps((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Clear all recorded steps
  const clearRecordedSteps = useCallback(() => {
    setRecordedSteps([]);
    stepCountRef.current = 0;
  }, []);

  return {
    isRecording,
    recordedSteps,
    startRecording,
    stopRecording,
    toggleRecording,
    recordClick,
    recordType,
    recordNavigate,
    recordWait,
    removeRecordedStep,
    clearRecordedSteps,
    rankSelectors,
  };
}
